import React, { useMemo, useState } from "react";
import { architecture, GROUP_COLORS } from "../../data/architecture";
import Reveal from "../chrome/Reveal";

// Nodes occupy y 60–474; the box is cropped to that plus a little air rather
// than leaving a band of dead space top and bottom.
const VIEW_BOX = "0 26 1000 486";

const byId = (id) => architecture.nodes.find((n) => n.id === id);
const center = (n) => ({ x: n.x + n.w / 2, y: n.y + n.h / 2 });

/**
 * Routes an edge between two boxes as an orthogonal path, leaving each box
 * from the correct edge rather than from its centre.
 */
function routeEdge(a, b) {
  const ca = center(a);
  const cb = center(b);
  const dx = cb.x - ca.x;
  const dy = cb.y - ca.y;

  // Predominantly horizontal: exit left/right, step through a midpoint.
  if (Math.abs(dx) > Math.abs(dy)) {
    const x1 = dx > 0 ? a.x + a.w : a.x;
    const x2 = dx > 0 ? b.x : b.x + b.w;
    const mid = (x1 + x2) / 2;
    return `M ${x1} ${ca.y} H ${mid} V ${cb.y} H ${x2}`;
  }

  // Predominantly vertical: exit top/bottom.
  const y1 = dy > 0 ? a.y + a.h : a.y;
  const y2 = dy > 0 ? b.y : b.y + b.h;
  const mid = (y1 + y2) / 2;
  return `M ${ca.x} ${y1} V ${mid} H ${cb.x} V ${y2}`;
}

const Architecture = () => {
  const [active, setActive] = useState("functions");

  const edges = useMemo(
    () =>
      architecture.edges.map((edge) => ({
        ...edge,
        d: routeEdge(byId(edge.from), byId(edge.to)),
      })),
    []
  );

  const node = byId(active);
  const connected = useMemo(() => {
    const set = new Set([active]);
    architecture.edges.forEach((e) => {
      if (e.from === active) set.add(e.to);
      if (e.to === active) set.add(e.from);
    });
    return set;
  }, [active]);

  return (
    <section className="section" id="architecture">
      <div className="shell">
        <Reveal className="section__head">
          <span className="eyebrow">{architecture.label}</span>
          <h2 className="section__title">
            What I actually <span className="accent-text">built</span>
          </h2>
          <p className="section__lede">
            {architecture.summary} Select any piece to see what it did.
          </p>
        </Reveal>

        <Reveal className="arch">
          <div className="arch__canvas">
            <svg
              viewBox={VIEW_BOX}
              className="arch__svg"
              role="img"
              aria-label={`Architecture diagram of the ${architecture.title} at ${architecture.company}`}
            >
              <defs>
                <marker
                  id="arch-arrow"
                  viewBox="0 0 10 10"
                  refX="9"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
                </marker>
              </defs>

              <g className="arch__edges">
                {edges.map((edge) => {
                  const lit = edge.from === active || edge.to === active;
                  return (
                    <g key={`${edge.from}-${edge.to}`} data-lit={lit}>
                      <path
                        className="arch__edge"
                        d={edge.d}
                        strokeDasharray={edge.dashed ? "5 6" : undefined}
                        markerEnd="url(#arch-arrow)"
                      />
                      {/* A packet runs the path only while the edge is lit. */}
                      {lit && <circle className="arch__packet" r="3.6">
                        <animateMotion dur="1.8s" repeatCount="indefinite" path={edge.d} />
                      </circle>}
                    </g>
                  );
                })}
              </g>

              <g>
                {architecture.nodes.map((n) => {
                  const isActive = n.id === active;
                  const isNear = connected.has(n.id);
                  return (
                    <g
                      key={n.id}
                      className="arch__node"
                      data-active={isActive}
                      data-near={isNear}
                      style={{ "--node-color": GROUP_COLORS[n.group] }}
                      onMouseEnter={() => setActive(n.id)}
                      onFocus={() => setActive(n.id)}
                      onClick={() => setActive(n.id)}
                      tabIndex={0}
                      role="button"
                      aria-pressed={isActive}
                      aria-label={`${n.label} — ${n.sub}`}
                    >
                      <rect x={n.x} y={n.y} width={n.w} height={n.h} rx="14" />
                      <text x={n.x + n.w / 2} y={n.y + 32} className="arch__label">
                        {n.label}
                      </text>
                      <text x={n.x + n.w / 2} y={n.y + 52} className="arch__sub">
                        {n.sub}
                      </text>
                    </g>
                  );
                })}
              </g>
            </svg>
          </div>

          <aside className="arch__panel" aria-live="polite">
            <p className="arch__panel-eyebrow" style={{ color: GROUP_COLORS[node.group] }}>
              {node.group}
            </p>
            <h3 className="arch__panel-title">{node.label}</h3>
            <p className="arch__panel-body">{node.detail}</p>
            <ul className="chip-row">
              {node.stack.map((tech) => (
                <li key={tech} className="chip chip--accent">{tech}</li>
              ))}
            </ul>
            <p className="arch__panel-foot">
              {architecture.title} · {architecture.company}
            </p>
          </aside>
        </Reveal>
      </div>
    </section>
  );
};

export default Architecture;
